import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import SMCLabel from '../../../components/Simple/SMCLabel';
import SMCModalBody from '../../../components/core/SMCModal/SMCModalBody';
import {useTranslation} from 'react-i18next';
import SMCButton from '../../../components/Simple/SMCButton';
import SMCModalButtonContainer from '../../../components/core/SMCModal/SMCModalButtonContainer';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Api from '../../../api/SMPFetchHelper';
import {enqueueFileDownload, getAxiosFilename, getErrorCode, hasValue} from '../../../utils/utils';
import {LinearProgress} from '@mui/material';
import loadingSpinner from '../../../assets/images/loading_spinner.gif';
import downloadIcon from '../../../assets/images/download.svg';
import RowLayout from '../../../components/Layouts/RowLayout';

function ExportSMPStepThree({
    onCancel,
    pageParams
}) {
    const { t } = useTranslation();

    const {
        isExportFirmware,
        isExportAdministrators,
        isExportSystemSettings,
        serviceDomainsToExportType,
        serviceDomainToExport,
        isExportUsers,
        isExportGateways,
        isExportPlans,
        isExportItems,
        isExportSettings
    } = pageParams;

    const [taskId, setTaskId] = useState(null);
    const [inProgress, setInProgress] = useState(true);
    const [isFetchingXmlData, setIsFetchingXmlData] = useState(false);
    const [stopped, setStopped] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [currentNumber, setCurrentNumber] = useState(null);
    const [currentMax, setCurrentMax] = useState(null);
    const [currentDisplayType, setCurrentDisplayType] = useState(null);
    const [currentErrorCount, setCurrentErrorCount] = useState(null);
    const [xmlData, setXmlData] = useState(null);
    const [xmlName, setXmlName] = useState(null);

    const canceled = useRef(false);

    const handleDismiss = useCallback(() => {
        canceled.current = true;
        onCancel();
    }, [onCancel]);

    const handleStop = useCallback(() => {
        if (window.confirm(t('SMC_SYSOPS_EXPWIZ_DO_STOP'))) {
            if (taskId) {
                Api.exportDBFileStop(taskId);
            }
        }
    }, [taskId, t]);

    const startExport = useCallback(async () => {
        try {
            const exportDBFileStartData = {
                isUsers: isExportUsers,
                isGateways: isExportGateways,
                isPlans: isExportPlans,
                isItems: isExportItems,
                isSettings: isExportSettings,
                isAdmins: isExportAdministrators,
                isFirms: isExportFirmware,
                isSystem: isExportSystemSettings,
                serviceDomain: serviceDomainToExport,
                serviceDomainType: serviceDomainsToExportType
            }
            const resp = await Api.exportDBFileStart(exportDBFileStartData);
            const taskId = resp.data.data.taskID;
            setTaskId(taskId);
            const checkProgress = async () => {
                if (canceled.current === true) {
                    return;
                }
                try {
                    const resp = await Api.exportDBFileGetProgress(taskId);
                    const respData = resp.data.data;
                    console.log(respData.message)
                    if (respData.message === 'STATE_RUNNING') {
                        const {currentNumber, currentDisplayType, currentMax, currentError} = respData;
                        setCurrentNumber(currentNumber);
                        setCurrentDisplayType(currentDisplayType);
                        setCurrentMax(currentMax);
                        setCurrentErrorCount(currentError);
                        setTimeout(checkProgress, 100);
                    }
                    else if (respData.message === 'STATE_DONE') {
                        setInProgress(false);
                        setIsFetchingXmlData(true);
                        const resp = await Api.exportDBFileDone(taskId);
                        const fileName = getAxiosFilename(resp) || 'smpexport.xml';
                        setXmlData(resp.data);
                        setXmlName(fileName);
                        setIsFetchingXmlData(false);
                    }
                    else if (respData.message === 'STATE_STOPPED') {
                        setInProgress(false);
                        setStopped(true);
                    }
                } catch (e) {
                    setHasError(true);
                    const errorCode = getErrorCode(e) || 'GENERAL_ERROR';
                    setErrorMessage(t(errorCode));
                }
            }
            checkProgress();
        } catch (e) {
            setHasError(true);
            const errorCode = getErrorCode(e) || 'GENERAL_ERROR';
            setErrorMessage(t(errorCode));
        }
    }, [isExportUsers, isExportGateways, isExportPlans, isExportItems, isExportSettings, isExportAdministrators, isExportFirmware, isExportSystemSettings, serviceDomainsToExportType, serviceDomainToExport, t]);

    useEffect(() => {
        startExport();
    }, [startExport]);

    const headerText = useMemo(() => {
        if (hasError) {
            return t('SMC_SYSOPS_EXPWIZ_DO_DONE_TEXT_ERR');
        }
        else if (stopped) {
            return t('SMC_SYSOPS_EXPWIZ_DO_DONE_HEAD');
        }
        else if (inProgress) {
            return t('SMC_SYSOPS_EXPWIZ_DO_WORK_HEAD');
        }
        else {
            return t('SMC_SYSOPS_EXPWIZ_DO_DONE_HEAD');
        }
    }, [hasError, inProgress, stopped, t]);

    const body = useMemo(() => {
        if (hasError) {
            return <SMCLabel>{errorMessage}. Please try again</SMCLabel>
        }
        else if (stopped) {
            return <SMCLabel>{t('SMC_SYSOPS_EXPWIZ_DO_STOPPED_TEXT')}</SMCLabel>
        }
        else if (inProgress) {
            if (hasValue(currentNumber) && currentMax && currentDisplayType && currentErrorCount) {
                const currentPercentage = Math.floor(currentNumber/currentMax*100);
                return (
                    <>
                    <SMCLabel>{t('SMC_PROGRESS_TEXT', [currentNumber, currentDisplayType, currentMax, currentPercentage, currentErrorCount])}</SMCLabel>
                    <LinearProgress style={{ width: '600px' }} variant="determinate" value={currentPercentage} />
                    </>
                );
            } else if (hasValue(currentNumber) && currentMax && currentDisplayType) {
                const currentPercentage = Math.floor(currentNumber/currentMax*100);
                return (
                    <>
                    <SMCLabel>{t('SMC_PROGRESS_TEXT_NOERR', [currentNumber, currentDisplayType, currentMax, currentPercentage])}</SMCLabel>
                    <LinearProgress style={{ width: '600px' }} variant="determinate" value={currentPercentage} />
                    </>
                )
            } else if (hasValue(currentNumber) && currentDisplayType && currentErrorCount) {
                return (
                    <>
                    <SMCLabel>{t('SMC_PROGRESS_TEXT_NOMAX', [currentDisplayType, currentNumber, currentErrorCount])}</SMCLabel>
                    <SMCLabel>{t('SMC_PROGRESS_WAIT')}</SMCLabel>
                    </>
                );
            } else if (hasValue(currentNumber) && currentDisplayType) {
                return (
                    <>
                    <SMCLabel>{t('SMC_PROGRESS_TEXT_NOMAX_NOERR', [currentDisplayType, currentNumber])}</SMCLabel>
                    <SMCLabel>{t('SMC_PROGRESS_WAIT')}</SMCLabel>
                    </>
                );
            }
        }
        else if (isFetchingXmlData) {
            return (
                <RowLayout>{t('SMC_SYSOPS_EXPWIZ_DO_DONW_FETCH')} <img src={loadingSpinner} alt="" style={{ height: 28, width: 28 }} /></RowLayout>
            );
        }
        else if (xmlData) {
            return (
                <SMCButton tooltip={t('SMC_GEN_DOWNLOAD')} variant="ghost" onClick={() => enqueueFileDownload(xmlData, xmlName)}>
                    <RowLayout spacing="4px">
                        <img src={downloadIcon} alt="download" width="16px" height="16px" />
                        <SMCLabel bold inline>Click to download</SMCLabel>
                    </RowLayout>
                </SMCButton>
            );
        }
        else {
            return null;
        }
    }, [hasError, errorMessage, inProgress, stopped, xmlData, xmlName, currentNumber, currentMax, currentDisplayType, currentErrorCount, isFetchingXmlData, t]);

    return (
        <>
        <SMCModalBody lineHeight="24px">
            <SMCLabel bold>{headerText}</SMCLabel>
            { body }
        </SMCModalBody>
        <SMCModalButtonContainer>
            {
                inProgress && (
                    <SMCButton disabled={!taskId} onClick={handleStop}>
                        <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="stop" />
                        {t('SMC_GEN_STOP')}
                    </SMCButton>
                )
            }
            <SMCButton onClick={handleDismiss}>
                <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                {t('SMC_GEN_CLOSE')}
            </SMCButton>
        </SMCModalButtonContainer>
        </>
    )
}

export default ExportSMPStepThree;